import React, { useRef, useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import Card from '@lwt-helix/card';
import Collapse from '@lwt-helix/collapse';
import { Button } from '@lwt-helix/buttons';
import Alert from '@lwt-helix/alert';
import BaseForm from '@lwt-helix/form-schema';
import { authServiceApiFactory } from '../../auth/authServiceApiFactory';
import { useServiceApi } from '../../hooks/services';
import { storeToken } from '../../functions/AuthenticationFunctions';
import { useDispatch, useSelector } from 'react-redux';
import {
    loggingIn,
    loginFailed,
    loginSuccess,
    resetPasswordFailed,
    resetPasswordSuccess,
    resettingPassword
} from '../../actions/AuthActions';
import Routes from '../../constants/Routes';
import { useAuth0 } from '@auth0/auth0-react';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

const Login = props => {
    const { history } = props;
    const authServiceApi = useServiceApi(authServiceApiFactory);
    const dispatch = useDispatch();
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const submitButtonRef = useRef();
    const resetPasswordRef = useRef();
    const toggleForgotPassword = () => {
        setForgotPasswordOpen(prevForgotPasswordOpen => !prevForgotPasswordOpen);
    };

    const { loginWithRedirect } = useAuth0();

    const error = useSelector(state => state.authenticationReducer.error);
    const isPasswordReset = useSelector(state => state.authenticationReducer.isPasswordReset);
    const { isLoading, isAuthenticated, getIdTokenClaims } = useAuth0();

    const storeIdToken = idToken => {
        storeToken(idToken);
        dispatch(loginSuccess({}));
        history.push(Routes.LOGIN_SUCCESS);
    };

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            getIdTokenClaims()
                .then(claims => {
                    storeIdToken(claims.__raw);
                });
        }

        if (!isLoading && !isAuthenticated && error) {
            dispatch(loginFailed(error));
        }
    }, [isLoading, isAuthenticated]);

    const handleFormSubmit = async (formData) => {
        if (formData.emailAddress.endsWith('@lwolf.com')) {
            dispatch(loggingIn());
            await loginWithRedirect({
                authorizationParams: {
                    redirect_uri: `${window.location.origin}/login`,
                    login_hint: formData.emailAddress
                }
            });
        } else {
            dispatch(loggingIn());
            await authServiceApi.login(formData).then(response => {
                if (!response.error) {
                    storeToken(response.tokens.token);
                    dispatch(loginSuccess(response.tokens));
                    history.push(Routes.LOGIN_SUCCESS);
                } else {
                    dispatch(loginFailed(response.error));
                }
            });
        }
    };

    const handleResetPassword = async (formData) => {
        dispatch(resettingPassword());
        const response = await authServiceApi.resetPassword(formData.emailAddress);
        if (response.succeeded) {
            dispatch(resetPasswordSuccess());
            toggleForgotPassword();
        } else {
            dispatch(resetPasswordFailed(response.error));
        }
    };

    const uiSchema = {
        emailAddress: {
            'ui:autofocus': true,
            'ui:title': 'Email Address',
            'lwolf:classNames': 'col-12',
            'ui:autocomplete': 'email'
        },
        password: {
            'ui:title': 'Password',
            'ui:widget': 'password',
            'lwolf:classNames': 'col-12',
            'ui:autocomplete': 'current-password'
        },
        'lwolf:rows': [
            ['emailAddress'],
            ['password']
        ]
    };

    const loginFormSchemaProps = {
        dataLwtId: 'login-form',
        schema: {
            type: 'object',
            required: ['emailAddress', 'password'],
            properties: {
                emailAddress: {
                    type: 'string',
                    title: 'Email Address',
                    format: 'email'
                },
                password: {
                    type: 'string',
                    title: 'Password',
                    minLength: 8
                }
            }
        },
        uiSchema: uiSchema,
        saveButtonRef: submitButtonRef,
        onSubmit: (formData) => handleFormSubmit(formData),
        idPrefix: 'login',
        id: 'login-form',
        customErrorMessages: {
            required: '{field} is a required field'
        }
    };

    const forgotPasswordSchemaProps = {
        dataLwtId: 'forgot-password-form',
        schema: {
            type: 'object',
            required: ['emailAddress'],
            properties: {
                emailAddress: {
                    type: 'string',
                    title: 'Email Address',
                    format: 'email'
                }
            }
        },
        uiSchema: {
            emailAddress: {
                'ui:title': 'Email Address',
                'ui:autocomplete': 'email'
            }
        },
        saveButtonRef: resetPasswordRef,
        onSubmit: (formData) => handleResetPassword(formData),
        idPrefix: 'forgot-pwd',
        id: 'forgot-pwd-form',
        customErrorMessages: {
            required: '{field} is a required field'
        }
    };

    const loginForm = <>
        <BaseForm {...loginFormSchemaProps} />
        <Button color="primary" onClick={() => submitButtonRef.current.click()} dataLwtId="submit">
            Sign In
        </Button>
    </>;

    const errorMsg = error && error.body?.code === 400
        ? 'Wrong email or password'
        : error?.code
            ? error?.message
            : 'Unknown error occurred';

    const forgotPassword = <>
        {!forgotPasswordOpen && <Button
            dataLwtId="forgot-password-button"
            color="link" className="forgotPasswordBtn text-decoration-none"
            onClick={() => toggleForgotPassword()}
            aria-controls="forgotPassword"
        >
            Forgot Password?
        </Button>
        }
        <Collapse dataLwtId="forgot-password-form" isOpen={forgotPasswordOpen}>
            <h4>Reset Password</h4>
            <p className="mb-2 text-muted">
                {'Enter the email address linked to your profile and ' +
                    'instructions will be emailed to you.'}
            </p>
            <BaseForm {...forgotPasswordSchemaProps} />
            <Button
                dataLwtId="forgot-password-button"
                color="primary"
                onClick={() => resetPasswordRef.current.click()}
            >
                Reset Password
            </Button>
            <Button
                dataLwtId="forgot-password-button"
                color="link" className="mt-3 d-block text-decoration-none"
                onClick={() => toggleForgotPassword()}
            >
                <i className="material-icons">arrow_back</i>
                Nevermind, I remember now.
            </Button>
        </Collapse>
    </>;


    return !isLoading ? (
        <div id="content-wrapper">
            <div className="container">
                <div className="vertical-center-form">
                    <Card
                        id="login-form"
                        dataLwtId="login-form"
                        raised
                        className="signin-form mt-5"
                        bodyProps={{
                            children: <>
                                <h2>Sign In</h2>
                                {loginForm}
                            </>
                        }}
                        footerProps={{
                            children: forgotPassword
                        }}
                    />
                </div>
                <div className="col-md-6 offset-md-3 mt-5">
                    {
                        error && <Alert
                            dataLwtId="error-alert"
                            fade={true}
                            isDismissable={true}
                            type="danger"
                            text={errorMsg}
                        />
                    }
                    {
                        isPasswordReset && <Alert
                            dataLwtId="password-reset-alert"
                            fade={true}
                            isDismissable={true}
                            type="info"
                            text="We've just sent you an email to reset your password"
                        />
                    }
                </div>
            </div>
        </div>
    ) : <LoadingOverlay loading={true} />
};

Login.propTypes = {
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
};

export default withRouter(Login);